import { SocialPlatform } from "@redotech/redo-content-builder-common/brand-kit";
import { cropConfigV2Schema } from "@redotech/redo-content-builder-common/crop-config-v2";
import {
  Alignment,
  buttonHierarchies,
  ElementVisibility,
  fontWeights,
} from "@redotech/redo-content-builder-common/element-schema";
import { ImageType } from "@redotech/redo-content-builder-common/image-type";
import {
  delayOptionSchema,
  SubscriptionDateFormat,
  SubscriptionSkipOrDelayState,
} from "@redotech/redo-marketing-email-builder-common/subscription-utils";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { Percentage } from "@redotech/util/percentage";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { SchemaType } from "./advanced-flow/schemas/schemas";
import { categories } from "./advanced-flow/triggers";
import {
  ButtonLinkType,
  CartLayoutType,
  EmailBlockType,
  EmailBuilderFontWeight,
  EmailHeaderType,
  ObjectFit,
  Orientation,
  ProductSelectionType,
  QrCodeLinkType,
  ScratcherSize,
  ShoppableProductsSize,
  Size,
  SocialIconColor,
  SocialItemSource,
  TableBorderStyle,
  TableColumnWidthMode,
  TableMobileStacking,
  TableMode,
  TableVerticalAlignment,
  TemplateType,
  VerticalAlignment,
} from "./email-builder";

export const defaultCreateGiftReceiptButton: BrandKitEmailButton = {
  override: {
    enabled: false,
    textColor: "#FFFFFF",
    border: { cornerRadiusPx: 0, stroke: { color: "#000000", weightPx: 0 } },
    paddingPx: 0,
    font: { fontFamily: "Arial", fontSizePx: 16, fontWeight: "regular" },
  },
  fullWidth: false,
  text: "Get Gift Receipt",
  hierarchy: "secondary",
};
export interface EmailTemplateRow extends EmailTemplate {
  _id: any;
}
const paddingSchema_ = z.object({
  top: z.number().min(0),
  right: z.number().min(0),
  bottom: z.number().min(0),
  left: z.number().min(0),
});
export const paddingSchema: z.ZodType<Padding> = paddingSchema_;
export interface Padding extends z.infer<typeof paddingSchema_> {}

const baseSectionSchema = z.object({
  blockId: zExt.objectId(),
  sectionPadding: paddingSchema,
  sectionColor: z.string(),
  visibility: createEnum(ElementVisibility, {
    enumName: "ElementVisibility",
  }).nullish(),
});

export const discountSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.DISCOUNT),
  discountId: zExt.objectId().optional(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  fontFamily: z.string(),
  fontWeight: createEnum(EmailBuilderFontWeight, {
    enumName: "EmailBuilderFontWeight",
  }),
  fontSize: z.number(),
  textColor: z.string(),
  blockBackgroundColor: z.string(),
});
export const discountSchema: z.ZodType<EmailBlock.Discount> = discountSchema_;

export const columnSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.COLUMN),
  columns: z.array(
    z.lazy(
      (): z.ZodType<NonRecursiveEmailBlock | null> =>
        nonRecursiveEmailBlocks.nullable(),
    ),
  ),
  columnCount: z.number(),
  gap: z.number(),
  stackOnMobile: z.boolean(),
  alignment: createEnum(VerticalAlignment, { enumName: "VerticalAlignment" }),
  columnWidths: z
    .array(
      z
        .number()
        .min(0)
        .max(100)
        .transform((n) => n as Percentage),
    )
    .nullish(), // note: there may be lingering bugs with this - the frontend had it long before the Zod schema did, due to an oversight
});
export const columnSchema: z.ZodType<EmailBlock.Column> = columnSchema_;

export const menuItemSchema = z.object({ id: z.string(), label: z.string() });

export const menuSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.MENU),
  menuItems: z.array(menuItemSchema),
  linkColor: z.string(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  fontFamily: z.string(),
  fontSize: z.number(),
  textColor: z.string(),
  stackOnMobile: z.boolean(),
  itemSpacing: z.number().optional(),
  useCustomSpacing: z.boolean().optional(),
});
export const menuSchema: z.ZodType<EmailBlock.Menu> = menuSchema_;

const qrCodeSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.QR_CODE),
  url: z.string().optional(),
  linkType: createEnum(QrCodeLinkType, { enumName: "QrCodeLinkType" }),
  schemaFieldName: z.string().optional(),
});
export const qrCodeSchema: z.ZodType<EmailBlock.QrCode> = qrCodeSchema_;

export const socialItemSchema = z.object({
  id: z.string(),
  platform: createEnum(SocialPlatform, { enumName: "SocialPlatform" }),
  url: z.string(),
  hidden: z.boolean().optional(),
  source: createEnum(SocialItemSource, {
    enumName: "SocialItemSource",
  }).optional(),
});

export const socialsSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SOCIALS),
  socialLinks: z.array(socialItemSchema),
  iconColor: createEnum(SocialIconColor, { enumName: "SocialIconColor" }),
  iconPadding: z.number(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  useBrandKitSocials: z.boolean().optional(),
});
export const socialsSchema: z.ZodType<EmailBlock.Socials> = socialsSchema_;

export const headerSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.HEADER),
  headerType: createEnum(EmailHeaderType, { enumName: "EmailHeaderType" }),
  layout: createEnum(Alignment, { enumName: "Alignment" }),
  imageUrl: z.string(),
  text: z.string(),
  textColor: z.string(),
  fontSize: z.number(),
  fontFamily: z.string(),
  logoHeight: z.number(),
  imageHeight: z.number(),
  altText: z.string().optional(),
  clickthroughUrl: z.string().optional(),
});
export const headerSchema: z.ZodType<EmailBlock.Header> = headerSchema_;

export const footerSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.FOOTER),
  horizontalPadding: createEnum(Size, { enumName: "Size" }),
  verticalPadding: createEnum(Size, { enumName: "Size" }),
  padding: paddingSchema,
  textColor: z.string(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  fontSize: z.number().optional(),
  fontFamily: z.string().optional(),
  schemaFieldName: z.string().optional(),
  useTemplateAddress: z.boolean().optional(),
});
export const footerSchema: z.ZodType<EmailBlock.Footer> = footerSchema_;

export const textSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.TEXT),
  textColor: z.string(),
  fontSize: z.number(),
  fontFamily: z.string(),
  linkColor: z.string(),
  text: z.string(),
});
export const textSchema: z.ZodType<EmailBlock.Text> = textSchema_;

export const buttonSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.BUTTON),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  cornerRadius: z.number(),
  buttonText: z.string(),
  padding: paddingSchema,
  buttonLink: z.string().optional(),
  fillColor: z.string(),
  strokeColor: z.string(),
  textColor: z.string(),
  strokeWeight: z.number(),
  fontFamily: z.string(),
  fontSize: z.number(),
  as: createEnum(["button", "a"], { enumName: "ButtonAs" }).optional(),
  buttonType: createEnum(["button", "submit", "reset"], {
    enumName: "ButtonType",
  }).optional(),
  fullWidth: z.boolean().optional(),
  linkType: createEnum(ButtonLinkType, { enumName: "ButtonLinkType" }),
  schemaFieldName: z.string().optional(),
});
export const buttonSchema: z.ZodType<EmailBlock.Button> = buttonSchema_;

const inlineButtonSchema_ = buttonSchema_.omit({
  blockId: true,
  sectionPadding: true,
  type: true,
  sectionColor: true,
  linkType: true,
});
export const inlineButtonSchema: z.ZodType<EmailBlock.InlineButton> =
  inlineButtonSchema_;

const reviewRequestSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.REVIEW_REQUEST),
  sectionHeader: z.string(),
  imageCornerRadius: z.number(),
  imageSize: createEnum(Size, { enumName: "Size" }),
  buttonColor: z.string(),
  buttonTextColor: z.string(),
  buttonCornerRadius: z.number(),
  buttonPadding: paddingSchema,
  textColor: z.string(),
  fontFamily: z.string(),
  imageAspectRatio: z.number().nullish(),
  imageObjectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
  schemaFieldName: z.string().optional(),
});

export const lineSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.LINE),
  color: z.string(),
  padding: paddingSchema,
  horizontalPadding: createEnum(Size, { enumName: "Size" }),
  verticalPadding: createEnum(Size, { enumName: "Size" }),
});
export const lineSchema: z.ZodType<EmailBlock.Line> = lineSchema_;

export const spacerSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SPACER),
  height: z.number(),
});
export const spacerSchema: z.ZodType<EmailBlock.Spacer> = spacerSchema_;

// Define the pixel crop schema
const pixelCropSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  unit: z.literal("px"),
});

// Define the crop config schema
const cropConfigV1Schema_ = z.object({
  crop: pixelCropSchema,
  imageWidth: z.number(), // even if the true image source is larger, it will be scaled down to this width
  imageHeight: z.number(), // even if the true image source is larger, it will be scaled down to this height
  circularCrop: z.boolean(),
  // Ideally we would not store these width ratios, they are incidental complexity caused by the frontend.
  // We should probably get rid of them at some point by dividing imageWidth and imageHeight by them, and then setting these to 1.
  cropWidthRatio: z.number(),
  cropHeightRatio: z.number(),
});
const cropConfigV1Schema: z.ZodType<CropConfigV1> = cropConfigV1Schema_;

/**
 * @deprecated
 * If you're reading this after July 2025, there is no reason to keep this interface around.
 */
export interface CropConfigV1 extends z.infer<typeof cropConfigV1Schema_> {}

export type CropConfigV2 = z.infer<typeof cropConfigV2Schema>;

/**
 * Converts a V1 crop config to V2 format
 * @param v1 The V1 crop config to convert
 * @returns V2 crop config with ratio-based coordinates
 */
export function convertCropConfigV1ToV2(v1: CropConfigV1): CropConfigV2 {
  // Apply the crop ratios to convert from displayed coordinates to natural image coordinates
  const adjustedCropX = v1.crop.x / v1.cropWidthRatio;
  const adjustedCropY = v1.crop.y / v1.cropHeightRatio;
  const adjustedCropWidth = v1.crop.width / v1.cropWidthRatio;
  const adjustedCropHeight = v1.crop.height / v1.cropHeightRatio;

  const widthRatio = adjustedCropWidth / v1.imageWidth;
  const heightRatio = adjustedCropHeight / v1.imageHeight;

  return {
    xRatio: adjustedCropX / v1.imageWidth,
    yRatio: adjustedCropY / v1.imageHeight,
    widthRatio,
    heightRatio,
    circularCrop: v1.circularCrop,
    baseImage: { width: v1.imageWidth, height: v1.imageHeight },
  };
}

export const imageSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.IMAGE),
  imageUrl: z.string(),
  croppedImageUrl: z.string().optional(),
  padding: paddingSchema,
  horizontalPadding: createEnum(Size, { enumName: "Size" }),
  verticalPadding: createEnum(Size, { enumName: "Size" }),
  showCaption: z.boolean(),
  caption: z.string().optional(),
  altText: z.string().optional(),
  clickthroughUrl: z.string().optional(),
  clickthroughLinkType: createEnum(ButtonLinkType, {
    enumName: "ButtonLinkType",
  }).optional(),
  clickthroughSchemaFieldName: z.string().optional(),
  aspectRatio: z.number().optional(),
  cropConfig: cropConfigV1Schema.optional(),
  cropConfigV2: cropConfigV2Schema.optional(),
  imageSourceType: createEnum(ImageType, { enumName: "ImageType" }).optional(),
});
export const imageSchema: z.ZodType<EmailBlock.Image> = imageSchema_;

// ---------------------------------------------------------------------------
// Table block (Klaviyo parity). Cells are image or text only. In dynamic mode
// the table iterates `rowCollection` (a path into the event payload) and binds
// each cell template against the current item under the fixed `item` alias
// (e.g. {{ item.image.src }}).
// ---------------------------------------------------------------------------
export const tableImageCellSchema = z.object({
  cellType: z.literal("image"),
  // Template; dynamic mode references the row alias, e.g. "{{ item.image_url }}".
  src: z.string(),
  altText: z.string().nullish(),
  // Optional clickthrough; dynamic mode e.g. "{{ item.review_link }}".
  clickthroughUrl: z.string().nullish(),
  widthPx: z.number().nullish(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
});

export const tableTextCellSchema = z.object({
  cellType: z.literal("text"),
  // Template; dynamic mode references the row alias, e.g. "{{ item.name }}".
  content: z.string(),
  textColor: z.string(),
  fontFamily: z.string(),
  fontSize: z.number(),
  fontWeight: createEnum(EmailBuilderFontWeight, {
    enumName: "EmailBuilderFontWeight",
  }),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
});

const tableCellSchema = z.discriminatedUnion("cellType", [
  tableImageCellSchema,
  tableTextCellSchema,
]);

const tableColumnSchema = z.object({
  id: z.string(),
  widthMode: createEnum(TableColumnWidthMode, {
    enumName: "TableColumnWidthMode",
  }),
  width: z.number().min(0).max(100).nullish(), // percentage when manual
  // Header cell for this column (shown when showHeaderRow is true). Kept on the
  // column rather than a parallel array so it can't drift out of length.
  // Optional so pre-existing table blocks (no per-column header) still parse.
  headerLabel: z.string().nullish(),
  headerAlignment: createEnum(Alignment, { enumName: "Alignment" }).nullish(),
});

export const tableSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.TABLE),
  mode: createEnum(TableMode, { enumName: "TableMode" }),
  columns: z.array(tableColumnSchema),
  // dynamic mode:
  rowCollection: z.string().nullish(), // path into the event, e.g. "properties.order_products"
  // Cap on how many rows to render from the collection; nullish = show all.
  maxRows: z.number().int().positive().nullish(),
  // Cell template, one entry per column. Repeated per item in dynamic mode.
  dynamicRowTemplates: z.array(tableCellSchema),
  // static mode: an explicit grid of cells ([row][column]).
  staticRows: z.array(z.array(tableCellSchema)),
  // Optional header row; per-column label + alignment live on each column.
  showHeaderRow: z.boolean(),
  headerTextColor: z.string(),
  headerBackgroundColor: z.string(),
  // Header-specific text styling + padding (applies to all header cells).
  // Nullish so table blocks saved before header styling still parse; the
  // renderer falls back to sensible defaults (bold weight, cell padding).
  headerFontSize: z.number().nullish(),
  headerBold: z.boolean().nullish(),
  headerItalic: z.boolean().nullish(),
  headerUnderline: z.boolean().nullish(),
  headerStrikethrough: z.boolean().nullish(),
  headerPadding: paddingSchema.nullish(),
  // Cell styling (Klaviyo: cell padding + borders).
  cellPadding: paddingSchema,
  cellVerticalAlignment: createEnum(TableVerticalAlignment, {
    enumName: "TableVerticalAlignment",
  }),
  borderColor: z.string().nullish(),
  borderWidth: z.number(),
  borderStyle: createEnum(TableBorderStyle, { enumName: "TableBorderStyle" }),
  // Mobile column behavior: stack top-to-bottom in column order
  // ("left-to-right"), stack in reverse column order ("right-to-left"), or stay
  // side-by-side ("none", wrapped in an mj-group).
  mobileStacking: createEnum(TableMobileStacking, {
    enumName: "TableMobileStacking",
  }),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
});

const trackingInfoSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.TRACKING_INFO),
  textColor: z.string(),
  buttonVisible: z.boolean(),
  buttonColor: z.string(),
  buttonTextColor: z.string(),
  buttonText: z.string().optional(),
  buttonCornerRadius: z.number(),
  buttonPadding: paddingSchema,
  statusIndicatorColor: z.string(),
  statusIndicatorInnerColor: z.string(),
  hideDeliveryEstimate: z.boolean(),
  fontFamily: z.string(),
  schemaFieldName: z.string().optional(),
});

const brandKitEmailButtonSchema_ = z.object({
  hierarchy: createEnum(buttonHierarchies, { enumName: "ButtonHierarchy" }),
  override: z.object({
    enabled: z.boolean(),
    backgroundColor: z.string().nullish(),
    textColor: z.string(),
    border: z.object({
      cornerRadiusPx: z.number(),
      stroke: z.object({ color: z.string(), weightPx: z.number() }),
    }),
    paddingPx: z.number(),

    font: z.object({
      fontFamily: z.string(),
      fontSizePx: z.number(),
      fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
    }),
  }),
  fullWidth: z.boolean(),
  text: z.string(),
});
const brandKitEmailButtonSchema: z.ZodType<BrandKitEmailButton> =
  brandKitEmailButtonSchema_;
export interface BrandKitEmailButton extends z.infer<
  typeof brandKitEmailButtonSchema_
> {}

const cornerRadiusSchema = z.object({
  topLeftPx: z.number(),
  topRightPx: z.number(),
  bottomLeftPx: z.number(),
  bottomRightPx: z.number(),
});
export interface CornerRadius extends z.infer<typeof cornerRadiusSchema> {}

/**
 * V2 of BrandKitEmailButton with enhanced padding and corner radius support.
 * Changes from V1:
 * - override.paddingPx (number) → override.padding (Padding with top/right/bottom/left)
 * - override.border.cornerRadiusPx (number) → override.border.cornerRadius (CornerRadius with per-corner values)
 */
const brandKitEmailButtonV2Schema_ = brandKitEmailButtonSchema_
  .omit({ override: true })
  .extend({
    override: brandKitEmailButtonSchema_.shape.override
      .omit({ paddingPx: true, border: true })
      .extend({
        padding: paddingSchema,
        border: z.object({
          cornerRadius: cornerRadiusSchema,
          stroke: z.object({ color: z.string(), weightPx: z.number() }),
        }),
      }),
  });
const brandKitEmailButtonV2Schema: z.ZodType<BrandKitEmailButtonV2> =
  brandKitEmailButtonV2Schema_;
export interface BrandKitEmailButtonV2 extends z.infer<
  typeof brandKitEmailButtonV2Schema_
> {}

const trackableSummarySchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.TRACKABLE_SUMMARY),
  showOrderInformation: z.boolean(),
  showCostSummary: z.boolean(),
  showPaymentInformation: z.boolean(),
  showCustomerInformation: z.boolean(),
  showTnplItemsOnly: z.boolean().nullish(),
  showImage: z.boolean().optional(),
  showTitle: z.boolean().optional(),
  showVariant: z.boolean().optional(),
  showPrice: z.boolean().optional(),
  showQuantity: z.boolean().optional(),
  showCustomFields: z.boolean().optional(),
  showCompareAtPrice: z.boolean().optional(),
  showSavingsSummary: z.boolean().optional(),
  createGiftReceiptButton: brandKitEmailButtonSchema.default(
    defaultCreateGiftReceiptButton,
  ),
  showCreateGiftReceiptButton: z.boolean().optional(),
  paymentDelayed: z.boolean().nullish(),
  textColor: z.string(),
  fontFamily: z.string(),
  imageSize: createEnum(Size, { enumName: "Size" }),
  imageCornerRadius: z.number(),
  imageAspectRatio: z.number().nullish(),
  imageObjectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
  schemaFieldName: z.string().optional(),
  customFields: z.array(z.string()).optional(),
  compareAtPriceLabel: z.string().optional(),
  subtotalLabel: z.string().optional(),
  savingsLabel: z.string().optional(),
  headerTextTransform: z.enum(["none", "uppercase", "lowercase"]).optional(),
  orderDetailsTitleLabel: z.string().optional(),
  itemsTitleLabel: z.string().optional(),
  otherItemsTitleLabel: z.string().optional(),
});

const manuallySelectedProductSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
});

const productsSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.PRODUCTS),
  textColor: z.string(),
  fontFamily: z.string(),
  titleFontSize: z.number().optional(),
  imageCornerRadius: z.number(),
  checkoutButton: inlineButtonSchema,
  /** Only used on abandonment flows */
  showCheckoutButton: z.boolean().optional(),
  lineItemButtons: inlineButtonSchema,
  numberOfProducts: z.number(),
  imageSize: createEnum(Size, { enumName: "Size" }),
  productSelectionType: createEnum(ProductSelectionType, {
    enumName: "ProductSelectionType",
  }),
  showPrice: z.boolean().optional(),
  showTitle: z.boolean().optional(),
  showImage: z.boolean().optional(),
  showButton: z.boolean().optional(),
  showQuantity: z.boolean().optional(),
  layoutType: createEnum(CartLayoutType, {
    enumName: "CartLayoutType",
  }).optional(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  columns: z.number(),
  stackOnMobile: z.boolean(),
  recommendedProductFilterId: zExt.objectId().optional(),
  manuallySelectedProducts: z.array(manuallySelectedProductSchema),
  imageAspectRatio: z.number().nullish(),
  imageObjectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
  schemaFieldName: z.string().optional(),
  provider: createEnum(Provider, { enumName: "Provider" }).default(
    Provider.SHOPIFY,
  ),
});

/**
 * Settings used for product carousel rendering and sizing calculations.
 * This subset of fields is needed by components that render the carousel UI.
 */
const productCarouselSettingsSchema_ = z.object({
  size: createEnum(ShoppableProductsSize, {
    enumName: "ShoppableProductsSize",
  }),
  show: z.object({
    image: z.boolean(),
    title: z.boolean(),
    price: z.boolean(),
    description: z.boolean(),
    button: z.boolean(),
  }),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  cardCornerRadiusPx: z.number(),
  fontFamily: z.string(),
  textColor: z.string(),
  imageAspectRatio: z.number(),
  imageObjectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
});
export const productCarouselSettingsSchema: z.ZodType<ProductCarouselSettings> =
  productCarouselSettingsSchema_;
export interface ProductCarouselSettings extends z.infer<
  typeof productCarouselSettingsSchema_
> {}

/**
 * Shared fields between shoppable products and one-click upsell blocks.
 * Both blocks display product cards with similar styling and selection options.
 */
const productCarouselBaseSchema = productCarouselSettingsSchema_.extend({
  productSelectionType: createEnum(ProductSelectionType, {
    enumName: "ProductSelectionType",
  }),
  manuallySelectedProducts: z.array(manuallySelectedProductSchema),
  productFilterId: zExt.objectId().optional(),
  numberOfDynamicProducts: z.number(),
  schemaFieldName: z.string().optional(),
  provider: createEnum(Provider, { enumName: "Provider" }).default(
    Provider.SHOPIFY,
  ),
});

export const shoppableProductsSchema_ = baseSectionSchema
  .extend(productCarouselBaseSchema.shape)
  .extend({
    type: z.literal(EmailBlockType.SHOPPABLE_PRODUCTS),
    productButtons: brandKitEmailButtonSchema,
    checkoutButton: brandKitEmailButtonSchema,
  });
export const shoppableProductsSchema: z.ZodType<EmailBlock.ShoppableProducts> =
  shoppableProductsSchema_;

export enum FontSource {
  BODY = "body",
  HEADING = "heading",
  CUSTOM = "custom",
}

/**
 * Font settings with source selection (body/heading from brand kit, or custom).
 */
const emailFontSettingsSchema_ = z.object({
  source: createEnum(FontSource, { enumName: "FontSource" }),
  sizePx: z.number(),
  color: z.string(),
  // Used when source is "custom"
  override: z.object({
    family: z.string(),
    weight: createEnum(fontWeights, { enumName: "FontWeight" }),
  }),
  bold: z.boolean(),
  italic: z.boolean(),
  underline: z.boolean(),
  strikethrough: z.boolean(),
});
export const emailFontSettingsSchema: z.ZodType<EmailFontSettings> =
  emailFontSettingsSchema_;
export interface EmailFontSettings extends z.infer<
  typeof emailFontSettingsSchema_
> {}

export enum OneClickSubscriptionButtonState {
  ACTIVE = "active",
  SUBSCRIBED = "subscribed",
  EXPIRED = "expired",
}

const oneClickSubscriptionSchema_ = baseSectionSchema
  .extend(productCarouselSettingsSchema_.shape)
  .extend({
    type: z.literal(EmailBlockType.ONE_CLICK_SUBSCRIPTION),
    schemaFieldName: z.string().nullish(),
    buttons: z.object({
      active: brandKitEmailButtonV2Schema,
      subscribed: brandKitEmailButtonV2Schema,
      expired: brandKitEmailButtonV2Schema,
    }),
    // Text styling for product card content
    text: z.object({
      title: emailFontSettingsSchema,
      originalPrice: emailFontSettingsSchema,
      discountedPrice: emailFontSettingsSchema,
      description: emailFontSettingsSchema,
    }),
    // Frontend-only field to control which button state is shown in the builder preview
    frontendOnlyPreviewButtonState: createEnum(
      OneClickSubscriptionButtonState,
      { enumName: "OneClickSubscriptionButtonState" },
    ).nullish(),
    skipEmailIfEmpty: z.boolean().nullish(),
  });
export const oneClickSubscriptionSchema: z.ZodType<EmailBlock.OneClickSubscription> =
  oneClickSubscriptionSchema_;

const scratchToRevealSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SCRATCH_TO_REVEAL),
  discountId: zExt.objectId().optional(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  cornerRadiusPx: z.number(),
  fontFamily: z.string(),
  fontWeight: createEnum(EmailBuilderFontWeight, {
    enumName: "EmailBuilderFontWeight",
  }),
  fontSizePx: z.number(),
  textColor: z.string(),
  secretBackgroundColor: z.string(),
  scratchOffColor: z.string(),
  scratcherSize: createEnum(ScratcherSize, { enumName: "ScratcherSize" }),
  scratchOffAreaPadding: paddingSchema,
  fallbackText: z.string(),
});

const starColorSchema = z.object({ empty: z.string(), filled: z.string() });

// could be simplified by importing from brand-kit and doing some omit() and extend()
// but holding off since Zod is slow
const brandKitEmailInputSchema = z.object({
  override: z.object({
    enabled: z.boolean(),
    backgroundColor: z.string().optional(),
    textColor: z.string(),
    errorColor: z.string(),
    border: z.object({
      cornerRadiusPx: z.number(),
      stroke: z.object({ color: z.string(), weightPx: z.number() }),
    }),
    paddingPx: z.number(),
    font: z.object({
      fontFamily: z.string(),
      fontSizePx: z.number(),
      fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
    }),
  }),
  fullWidth: z.boolean(),
});

const interactiveReviewRequestSuccessBlockSchema: z.ZodType<InteractiveReviewRequestSuccessBlock> =
  z.discriminatedUnion("type", [
    textSchema_,
    imageSchema_,
    discountSchema_,
    buttonSchema_,
    lineSchema_,
    spacerSchema_,
  ]);

const interactiveReviewRequestSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.INTERACTIVE_REVIEW_REQUEST),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  layout: createEnum(Orientation, { enumName: "Orientation" }),
  star: z.object({
    pointRadius: z.number(),
    color: starColorSchema,
    outline: z.object({
      enabled: z.boolean(),
      widthPx: z.number(),
      color: z.string(),
    }),
  }),
  inputs: brandKitEmailInputSchema,
  placeholderText: z.object({ title: z.string(), description: z.string() }),
  submitButton: brandKitEmailButtonSchema,
  image: z.object({
    aspectRatio: z.number(),
    cornerRadiusPx: z.number(),
    size: createEnum(ShoppableProductsSize, {
      enumName: "ShoppableProductsSize",
    }),
    objectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
  }),
  font: z.object({
    weight: createEnum(fontWeights, { enumName: "FontWeight" }),
    family: z.string(),
    sizePx: z.number(),
  }),
  textColor: z.string(),
  schemaFieldName: z.string().optional(),
  /**
   * Blocks to display when a review is successfully submitted
   */
  successSection: z.object({
    blocks: z.array(interactiveReviewRequestSuccessBlockSchema),
    padding: paddingSchema,
    backgroundColor: z.string(),
  }),
});

const subscriptionSkipOrDelaySchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SUBSCRIPTION_SKIP_OR_DELAY),
  delayOptions: z.array(delayOptionSchema),
  chargeDate: z.object({
    enabled: z.boolean(),
    dateFormat: createEnum(SubscriptionDateFormat, {
      enumName: "SubscriptionDateFormat",
    }),
  }),
  skipOption: z.object({ enabled: z.boolean() }),
  delayedPage: z.object({ title: z.string(), subtitle: z.string() }),
  skippedPage: z.object({ title: z.string(), subtitle: z.string() }),
  initialPage: z.object({
    title: z.string(),
    buttons: brandKitEmailButtonSchema, // these settings also apply to buttons not on the initial page, confusingly
  }),
  font: z.object({
    weight: createEnum(fontWeights, { enumName: "FontWeight" }),
    family: z.string(),
    sizePx: z.number(),
  }),
  textColor: z.string(),
});
export interface SubscriptionSkipOrDelay extends z.infer<
  typeof subscriptionSkipOrDelaySchema
> {}

export type InteractiveReviewRequestSuccessBlock =
  | EmailBlock.Text
  | EmailBlock.Image
  | EmailBlock.Discount
  | EmailBlock.Button
  | EmailBlock.Line
  | EmailBlock.Spacer;

const subscriptionAddToOrderSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SUBSCRIPTION_ADD_TO_ORDER),
});

const subscriptionSwapProductSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SUBSCRIPTION_SWAP_PRODUCT),
});

const chromeDinoSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.CHROME_DINO),
});

const sectionReferenceSchema_ = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SECTION_REFERENCE),
  sectionId: zExt.objectId(),
  sectionName: z.string().optional(), // For display purposes
});
export const sectionReferenceSchema: z.ZodType<EmailBlock.SectionReference> =
  sectionReferenceSchema_;

const nonRecursiveEmailBlocks = z.discriminatedUnion("type", [
  discountSchema_,
  menuSchema_,
  socialsSchema_,
  headerSchema_,
  footerSchema_,
  textSchema_,
  buttonSchema_,
  reviewRequestSchema,
  lineSchema_,
  spacerSchema_,
  imageSchema_,
  trackingInfoSchema,
  trackableSummarySchema,
  productsSchema,
  shoppableProductsSchema_,
  oneClickSubscriptionSchema_,
  scratchToRevealSchema,
  subscriptionSkipOrDelaySchema,
  subscriptionAddToOrderSchema,
  subscriptionSwapProductSchema,
  chromeDinoSchema,
  sectionReferenceSchema_,
  qrCodeSchema_,
  tableSchema,
]);

const emailBlockSchema_ = z.discriminatedUnion("type", [
  ...nonRecursiveEmailBlocks.def.options,
  columnSchema_,
  interactiveReviewRequestSchema,
]);

export const emailBlockSchema: z.ZodType<EmailBlock> = emailBlockSchema_;
export interface IEmailBlockSchema extends z.ZodType<EmailBlock> {}

const emailFooterAddress = z.object({
  /** Business name that appears in email footer (e.g. "Acme Corp") */
  businessAddress: z.string().optional().nullable(),
  /** Street address including both lines if needed (e.g. "123 Main Street Suite 200") */
  legalAddress: z.string(),
  /** Combined city, state, and postal code (e.g. "New York, NY 10001") */
  cityStateZip: z.string(),
  /** Country name (e.g. "United States") */
  country: z.string(),
});
export type EmailFooterAddress = z.infer<typeof emailFooterAddress>;

export const emailTemplateSchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  subject: z.string(),
  templateType: createEnum(TemplateType, { enumName: "TemplateType" }),
  category: createEnum(categories, { enumName: "Category" }),
  schemaType: createEnum(SchemaType, { enumName: "SchemaType" }),
  emailPreview: z.string().optional().nullable(),
  emailBackgroundColor: z.string(),
  contentBackgroundColor: z.string(),
  address: emailFooterAddress.nullish(),
  sections: z.array(emailBlockSchema),
  linkColor: z.string().optional().nullable(),
  team: zExt.objectId().optional(),
  createdAt: z.coerce.date().nullish(),
  updatedAt: z.coerce.date().nullish(),
  originTemplateId: zExt.objectId().optional(),
  isPlainText: z.boolean().optional(),
});
export interface EmailTemplate extends z.infer<typeof emailTemplateSchema> {}

/**
 * Templates with these schema types will not show up in the global "email templates" or "sms templates" pages in the merchant dashboard.
 *
 * This array is not expected to live very long since we will be significantly refactoring the way we display templates in the near future.
 */
export const excludedFromListView: SchemaType[] = [
  SchemaType.MARKETING_CAMPAIGN,
];

/**
 * Type-safe mapping of EmailBlockType to schema field names
 * This ensures we have a clear mapping for each block type that requires a schemaFieldName
 */
type SchemaFieldNameMapping = Record<EmailBlockType, string | undefined>;

/**
 * Maps EmailBlockType to the corresponding schema field name
 * Add new mappings here when adding new email block types that require schemaFieldName
 */
const schemaFieldNameMap: SchemaFieldNameMapping = {
  [EmailBlockType.FOOTER]: "unsubscribeLink",
  [EmailBlockType.TRACKABLE_SUMMARY]: "orderSummary",
  [EmailBlockType.REVIEW_REQUEST]: "reviewRequest",
  [EmailBlockType.TRACKING_INFO]: "tracker",
  [EmailBlockType.PRODUCTS]: "cartContext",
  [EmailBlockType.SHOPPABLE_PRODUCTS]: "cartContext",
  [EmailBlockType.INTERACTIVE_REVIEW_REQUEST]: "reviewRequest",
  [EmailBlockType.DISCOUNT]: undefined,
  [EmailBlockType.COLUMN]: undefined,
  [EmailBlockType.LINE]: undefined,
  [EmailBlockType.MENU]: undefined,
  [EmailBlockType.SOCIALS]: undefined,
  [EmailBlockType.HEADER]: undefined,
  [EmailBlockType.TEXT]: undefined,
  [EmailBlockType.BUTTON]: undefined,
  [EmailBlockType.IMAGE]: undefined,
  [EmailBlockType.SPACER]: undefined,
  [EmailBlockType.SCRATCH_TO_REVEAL]: undefined,
  [EmailBlockType.SUBSCRIPTION_SKIP_OR_DELAY]: undefined,
  [EmailBlockType.SUBSCRIPTION_ADD_TO_ORDER]: undefined,
  [EmailBlockType.SUBSCRIPTION_SWAP_PRODUCT]: undefined,
  [EmailBlockType.CHROME_DINO]: undefined,
  [EmailBlockType.SECTION_REFERENCE]: undefined,
  [EmailBlockType.QR_CODE]: undefined,
  [EmailBlockType.ONE_CLICK_SUBSCRIPTION]: "orderSummary",
  // Table binds via rowCollection (a path), not a fixed schema field.
  [EmailBlockType.TABLE]: undefined,
};

/**
 * Gets the schema field name for a given email block type
 * @param blockType The email block type to get the schema field name for
 * @returns The schema field name if one exists for the block type, undefined otherwise
 */
export function getSchemaFieldNameForBlock(
  blockType: EmailBlockType,
): string | undefined {
  return schemaFieldNameMap[blockType];
}

export namespace EmailBlock {
  export interface Header extends z.infer<typeof headerSchema_> {}
  export interface Footer extends z.infer<typeof footerSchema_> {}
  export interface Text extends z.infer<typeof textSchema_> {}
  export interface Button extends z.infer<typeof buttonSchema_> {}
  export interface InlineButton extends z.infer<typeof inlineButtonSchema_> {}
  export interface Image extends z.infer<typeof imageSchema_> {}
  export interface Discount extends z.infer<typeof discountSchema_> {}
  export interface Column extends z.infer<typeof columnSchema_> {}
  export interface Menu extends z.infer<typeof menuSchema_> {}
  export interface QrCode extends z.infer<typeof qrCodeSchema_> {}
  export interface Socials extends z.infer<typeof socialsSchema_> {}
  export interface ReviewRequest extends z.infer<typeof reviewRequestSchema> {}
  export interface InteractiveReviewRequest extends z.infer<
    typeof interactiveReviewRequestSchema
  > {
    /**
     * This field is intentionally stripped out whenever Zod parses it or saved in the DB.
     * It serves as a way to force the email builder to show the "success" page even before submission.
     * The more general solution would be refactoring the email builder to deal with Builder<Section> instead,
     * but this is a quick hack because YAGNI.
     */
    frontendOnlyForceSuccessState?: boolean;
  }
  export interface Line extends z.infer<typeof lineSchema_> {}
  export interface Spacer extends z.infer<typeof spacerSchema_> {}
  export interface TrackingInfo extends z.infer<typeof trackingInfoSchema> {}
  export interface TrackableSummary extends z.infer<
    typeof trackableSummarySchema
  > {}
  export interface Products extends z.infer<typeof productsSchema> {}
  export interface ShoppableProducts extends z.infer<
    typeof shoppableProductsSchema_
  > {}
  export interface OneClickSubscription extends z.infer<
    typeof oneClickSubscriptionSchema_
  > {}
  export interface ScratchToReveal extends z.infer<
    typeof scratchToRevealSchema
  > {}
  export interface SubscriptionSkipOrDelay extends z.infer<
    typeof subscriptionSkipOrDelaySchema
  > {
    frontendOnlyForceState?: SubscriptionSkipOrDelayState;
  }
  export interface SubscriptionAddToOrder extends z.infer<
    typeof subscriptionAddToOrderSchema
  > {}
  export interface SubscriptionSwapProduct extends z.infer<
    typeof subscriptionSwapProductSchema
  > {}
  export interface ChromeDino extends z.infer<typeof chromeDinoSchema> {}
  export interface SectionReference extends z.infer<
    typeof sectionReferenceSchema_
  > {}
  export interface Table extends z.infer<typeof tableSchema> {}
  export interface TableImageCell extends z.infer<
    typeof tableImageCellSchema
  > {}
  export interface TableTextCell extends z.infer<typeof tableTextCellSchema> {}
}

export type NonRecursiveEmailBlock =
  | EmailBlock.Header
  | EmailBlock.Footer
  | EmailBlock.Text
  | EmailBlock.Button
  | EmailBlock.Line
  | EmailBlock.Spacer
  | EmailBlock.Image
  | EmailBlock.TrackingInfo
  | EmailBlock.TrackableSummary
  | EmailBlock.Products
  | EmailBlock.ReviewRequest
  | EmailBlock.QrCode
  | EmailBlock.Menu
  | EmailBlock.Socials
  | EmailBlock.Discount
  | EmailBlock.ShoppableProducts
  | EmailBlock.OneClickSubscription
  | EmailBlock.ScratchToReveal
  | EmailBlock.SubscriptionSkipOrDelay
  | EmailBlock.SubscriptionAddToOrder
  | EmailBlock.SubscriptionSwapProduct
  | EmailBlock.ChromeDino
  | EmailBlock.SectionReference
  | EmailBlock.Table;

export type RecursiveEmailBlock =
  | EmailBlock.Column
  | EmailBlock.InteractiveReviewRequest;

export type EmailBlock = NonRecursiveEmailBlock | RecursiveEmailBlock;
